/**
 * Base Maps API Service
 * 
 * Service for managing base maps through admin API endpoints.
 */

import { apiService } from './api';
import type { BaseMap, BaseMapFormData, ExtractedMetadata } from '../types/baseMaps';

interface ApiResponse<T> {
  data?: T;
  error?: string;
  statusCode: number;
}

class BaseMapsApiService {
  /**
   * List all base maps (admin endpoint)
   */
  async listBaseMaps(): Promise<ApiResponse<{ baseMaps: BaseMap[]; count: number }>> {
    return apiService.get<{ baseMaps: BaseMap[]; count: number }>('/admin/base-maps');
  }

  /**
   * Get base map details by ID
   */
  async getBaseMap(id: string): Promise<ApiResponse<{ baseMap: BaseMap }>> {
    return apiService.get<{ baseMap: BaseMap }>(`/admin/base-maps/${id}`);
  }

  /**
   * Create a new base map
   */
  async createBaseMap(data: BaseMapFormData): Promise<ApiResponse<{ baseMap: BaseMap }>> {
    return apiService.post<{ baseMap: BaseMap }>('/admin/base-maps', data);
  }

  /**
   * Update an existing base map
   */
  async updateBaseMap(id: string, data: Partial<BaseMapFormData>): Promise<ApiResponse<{ baseMap: BaseMap }>> {
    return apiService.put<{ baseMap: BaseMap }>(`/admin/base-maps/${id}`, data);
  }

  /**
   * Delete a base map
   */
  async deleteBaseMap(id: string): Promise<ApiResponse<void>> {
    return apiService.delete<void>(`/admin/base-maps/${id}`);
  }

  /**
   * Extract metadata from GeoTIFF file
   */
  async extractMetadata(id: string, geoTiffUrl: string): Promise<ApiResponse<{ metadata: ExtractedMetadata }>> {
    return apiService.post<{ metadata: ExtractedMetadata }>(
      `/admin/base-maps/${id}/extract-metadata`,
      { geoTiffUrl }
    );
  }

  /**
   * Get public base maps catalog (non-admin endpoint)
   */
  async getPublicCatalog(): Promise<ApiResponse<{ baseMaps: BaseMap[] }>> {
    return apiService.get<{ baseMaps: BaseMap[] }>('/api/base-maps');
  }
}

export const baseMapsApi = new BaseMapsApiService();
export default baseMapsApi;
